// @systeme: interface
// La double authentification de l'admin : à la première visite, le secret à saisir dans
// une application d'authentification (Aegis, Google Authenticator, Authy…) ; ensuite,
// le code à six chiffres, redemandé toutes les douze heures.
import { useEffect, useState, type FormEvent } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, useOutletContext } from 'react-router';
import type { EtatDeuxFacteurs } from '@foyer/shared';
import { Chargement } from '../../composants/Chargement';
import { useMessages } from '../../composants/Messages';
import { api } from '../../lib/api';

export function Verification() {
  const { direErreur } = useMessages();
  const navigate = useNavigate();
  const { surCodeVerifie } = useOutletContext<{ surCodeVerifie: () => void }>();
  const [etat, setEtat] = useState<EtatDeuxFacteurs | undefined>();
  const [code, setCode] = useState('');

  useEffect(() => {
    api.admin.deuxFacteurs().then(setEtat).catch(direErreur);
  }, [direErreur]);

  async function presenter(e: FormEvent) {
    e.preventDefault();
    try {
      await api.admin.presenterCode(code);
      surCodeVerifie();
      navigate('/admin', { replace: true });
    } catch (erreur) {
      setCode('');
      direErreur(erreur);
    }
  }

  if (!etat) return <Chargement />;
  return (
    <Stack component="form" spacing={2} onSubmit={(e) => void presenter(e)} sx={{ maxWidth: 420 }}>
      <Typography variant="h2" component="h2">
        {etat.active ? 'Ton code' : 'Mettre en place la double authentification'}
      </Typography>
      {etat.active ? (
        <Typography color="text.secondary">
          L’administration demande un code de ton application d’authentification, une fois toutes
          les douze heures.
        </Typography>
      ) : (
        <>
          <Typography color="text.secondary">
            Ouvre une application d’authentification (Aegis, Google Authenticator, Authy…), ajoute
            un compte « par clé » et saisis ce secret. Puis entre le code qu’elle affiche.
          </Typography>
          <TextField
            label="Secret à saisir"
            value={etat.secret ?? ''}
            InputProps={{ readOnly: true }}
            onFocus={(ev) => ev.target.select()}
          />
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            Ou colle cette adresse dans l’application : {etat.url}
          </Typography>
        </>
      )}
      <TextField
        label="Code à six chiffres"
        value={code}
        onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
        inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
        autoFocus
      />
      <Button type="submit" variant="contained" disabled={code.length !== 6}>
        Continuer
      </Button>
    </Stack>
  );
}
